const chapter = {
  id: 'surface-chemistry',
  slug: 'surface-chemistry',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Surface Chemistry',
        hindi: 'पृष्ठ रसायन',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'adsorption',
      title: {
        english: 'Adsorption — Physisorption vs Chemisorption',
        hindi: 'अधिशोषण — भौतिक बनाम रासायनिक अधिशोषण',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Adsorption / Absorption / Sorption',
        hindi: 'सोखना / अवशोषण / सोखना',
          },
          body: {
            english: 'Adsorption: accumulation of molecular species at the **surface** rather than in the bulk. Substance adsorbed = adsorbate; surface = adsorbent. Absorption is uniform throughout the bulk. Sorption = both occurring together (e.g. water on cotton).',
        hindi: 'सोखना: आणविक प्रजातियों का थोक के बजाय **सतह** पर संचय। पदार्थ adsorbed = adsorbate; surface = adsorbent. अवशोषण पूरे थोक में एक समान है। Sorption = both occurring together (e.g. कपास पर पानी)।',
          },
        },
        {
          type: 'table',
          headers: ['Property', 'Physisorption', 'Chemisorption'],
          rows: [
            ['Forces', 'van der Waals', 'Chemical (covalent / ionic)'],
            ['$\\Delta H_{ads}$ (kJ/mol)', '20–40 (low)', '80–240 (high)'],
            ['Specificity', 'Non-specific', 'Highly specific'],
            ['Reversibility', 'Reversible', 'Often irreversible'],
            ['Effect of $T$', 'Decreases with $T$', 'First $\\uparrow$ then $\\downarrow$ with $T$'],
            ['Activation energy', 'Negligible', 'Appreciable'],
            ['Layer formed', 'Multi-molecular', 'Mono-molecular'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Adsorption is always exothermic ($\\Delta H < 0$). Since $\\Delta S < 0$ (loss of freedom), spontaneity ($\\Delta G < 0$) requires sufficiently negative $\\Delta H$.',
        hindi: 'सोखना हमेशा ऊष्माक्षेपी होता है ($\\Delta H < 0$)। चूंकि $\\Delta S < 0$ (स्वतंत्रता की हानि), सहजता ($\\Delta G < 0$) के लिए पर्याप्त नकारात्मक $\\Delta H$ की आवश्यकता होती है।',
            },
            {
              english: 'Extent of adsorption $x/m$ depends on: nature of adsorbent, surface area, nature of adsorbate, temperature, pressure.',
        hindi: 'अधिशोषण की सीमा $x/m$ इस पर निर्भर करती है: अधिशोषक की प्रकृति, सतह क्षेत्र, अधिशोषित की प्रकृति, तापमान, दबाव।',
            },
          ],
        },
      ],
    },
    {
      id: 'isotherms',
      title: {
        english: 'Adsorption Isotherms',
        hindi: 'अधिशोषण समतापी',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Freundlich isotherm',
        hindi: 'फ्रायंडलिच इज़ोटेर्म',
          },
          statement: {
            english: '$\\dfrac{x}{m} = k\\,P^{1/n}$ where $0 < 1/n \\le 1$. Log form: $\\log(x/m) = \\log k + (1/n)\\log P$ — straight line with slope $1/n$. Limits: at low $P$, $1/n \\to 1$; at high $P$, $1/n \\to 0$ (saturation).',
        hindi: '$\\dfrac{x}{m} = k\\,P^{1/n}$ जहां $0 < 1/n \\le 1$. लॉग फॉर्म: $\\log(x/m) = \\log k + (1/n)\\log P$ - ढलान $1/n$ के साथ सीधी रेखा। सीमाएँ: निम्न $P$, $1/n \\to 1$ पर; उच्च $P$, $1/n \\to 0$ (संतृप्ति) पर।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Langmuir isotherm',
        hindi: 'लैंगमुइर इज़ोटेर्म',
          },
          statement: {
            english: '$\\dfrac{x}{m} = \\dfrac{aP}{1 + bP}$. Assumes monolayer coverage on uniform sites. At low $P$: $x/m \\propto P$; at high $P$: $x/m \\to a/b$ (saturation).',
        hindi: '$\\dfrac{x}{m} = \\dfrac{aP}{1 + bP}$. समान साइटों पर मोनोलेयर कवरेज मानता है। निम्न $P$ पर: $x/m \\propto P$; उच्च $P$: $x/m \\to a/b$ (संतृप्ति) पर।',
          },
        },
      ],
    },
    {
      id: 'catalysis',
      title: {
        english: 'Catalysis',
        hindi: 'उत्प्रेरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Homogeneous**: catalyst & reactants in same phase. e.g. lead chamber process for H₂SO₄ (NO catalyses oxidation of SO₂); ester hydrolysis catalysed by H⁺.',
        hindi: '**सजातीय**: उत्प्रेरक और अभिकारक एक ही चरण में। जैसे H₂SO₄ के लिए लेड चैम्बर प्रक्रिया (NO SO₂ के ऑक्सीकरण को उत्प्रेरित करता है); एस्टर हाइड्रोलिसिस H⁺ द्वारा उत्प्रेरित होता है। **विषम**: विभिन्न चरण। जैसे हैबर (Fe), संपर्क (V₂O₅), तेलों का हाइड्रोजनीकरण (Ni), ओस्टवाल्ड (NH₃ → NO के लिए Pt-Rh गेज)।',
            },
            {
              english: '**Heterogeneous**: different phase. e.g. Haber (Fe), Contact (V₂O₅), hydrogenation of oils (Ni), Ostwald (Pt-Rh gauze for NH₃ → NO).',
        hindi: '**विषम**: विभिन्न चरण। जैसे हैबर (Fe), संपर्क (V₂O₅), तेलों का हाइड्रोजनीकरण (Ni), ओस्टवाल्ड (NH₃ → NO के लिए Pt-Rh गेज)।',
            },
            {
              english: 'Mechanism on surface: (i) diffusion to surface, (ii) adsorption, (iii) chemical reaction at surface, (iv) desorption, (v) diffusion away.',
        hindi: 'सतह पर तंत्र: (i) सतह पर प्रसार, (ii) सोखना, (iii) सतह पर रासायनिक प्रतिक्रिया, (iv) विशोषण, (v) दूर तक प्रसार। **प्रवर्तक**: उत्प्रेरक की गतिविधि बढ़ाता है (हैबर में मो)। **ज़हर**: गतिविधि को कम कर देता है / मार देता है (जहर के रूप में)।',
            },
            {
              english: '**Promoter**: increases activity of catalyst (Mo in Haber). **Poison**: decreases / kills activity (As poisons Pt).',
        hindi: '**प्रवर्तक**: उत्प्रेरक की गतिविधि बढ़ाता है (हैबर में मो)। **ज़हर**: गतिविधि को कम कर देता है / मार देता है (जहर के रूप में)।',
            },
            {
              english: '**Shape-selective catalysis**: zeolites (e.g. ZSM-5) have pores that allow only molecules of specific shape to react — used in petrochemical industry.',
        hindi: '**आकार-चयनात्मक कटैलिसीस**: जिओलाइट्स (जैसे ZSM-5) में छिद्र होते हैं जो केवल विशिष्ट आकार के अणुओं को प्रतिक्रिया करने की अनुमति देते हैं - पेट्रोकेमिकल उद्योग में उपयोग किया जाता है।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Enzyme catalysis',
        hindi: 'एंजाइम कटैलिसीस',
          },
          body: {
            english: 'Highly specific protein catalysts. Highly efficient ($10^6$–$10^{20}$ rate enhancement). Optimum at body $T$ (~310 K) and definite pH. Mechanism: substrate binds at active site → enzyme-substrate complex → product (Michaelis–Menten).',
        hindi: 'अत्यधिक विशिष्ट प्रोटीन उत्प्रेरक। अत्यधिक कुशल ($10^6$–$10^{20}$ दर वृद्धि)। शरीर पर इष्टतम $T$ (~310 K) और निश्चित pH। तंत्र: सब्सट्रेट सक्रिय स्थल पर बंधता है → एंजाइम-सब्सट्रेट कॉम्प्लेक्स → उत्पाद (माइकलिस-मेंटेन)।',
          },
        },
      ],
    },
    {
      id: 'colloids',
      title: {
        english: 'Colloids — Classification & Properties',
        hindi: 'कोलॉइड — वर्गीकरण और गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Particle size: true solution $< 1$ nm; **colloid 1–1000 nm**; suspension $> 1000$ nm.',
        hindi: 'कण आकार: सही समाधान $< 1$ एनएम; **कोलाइड 1-1000 एनएम**; निलंबन $> 1000$ एनएम।',
            },
            {
              english: 'Two phases: **dispersed phase** (solute) + **dispersion medium** (solvent).',
        hindi: 'दो चरण: **फैला हुआ चरण** (विलेय) + **फैलाव माध्यम** (विलायक)।',
            },
            {
              english: 'Classification by physical state: 8 types (sol/gel/aerosol/foam/emulsion etc.). Sol = solid in liquid (paint, blood); Gel = liquid in solid (jelly, butter); Aerosol = liquid in gas (mist) or solid in gas (smoke); Emulsion = liquid in liquid (milk, mayonnaise).',
        hindi: 'भौतिक अवस्था के आधार पर वर्गीकरण: 8 प्रकार (सोल/जेल/एरोसोल/फोम/इमल्शन आदि)। सोल = तरल में ठोस (पेंट, रक्त); जेल = ठोस में तरल (जेली, मक्खन); एरोसोल = गैस में तरल (धुंध) या गैस में ठोस (धुआं); इमल्शन = तरल में तरल (दूध, मेयोनेज़)।',
            },
            {
              english: 'By interaction with solvent: **lyophilic** (solvent-loving, reversible, stable — gum, gelatin) vs **lyophobic** (solvent-hating, irreversible, easily coagulated — metal sols, As₂S₃).',
        hindi: 'विलायक के साथ अंतःक्रिया द्वारा: **लियोफिलिक** (विलायक-प्रेमी, प्रतिवर्ती, स्थिर - गोंद, जिलेटिन) बनाम **लियोफोबिक** (विलायक-घृणित, अपरिवर्तनीय, आसानी से जमने वाला - धातु सॉल, As₂S₃)।',
            },
            {
              english: 'By dispersed-phase structure: multimolecular (Au sol), macromolecular (proteins, starch, polymers), associated colloids / **micelles** (soap above CMC).',
        hindi: 'बिखरी-चरण संरचना द्वारा: बहुआणविक (एयू सोल), मैक्रोमोलेक्यूलर (प्रोटीन, स्टार्च, पॉलिमर), संबंधित कोलाइड / **मिसेल** (सीएमसी के ऊपर साबुन)।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Properties of colloids**:',
        hindi: '**कोलाइड के गुण**:',
            },
            {
              english: '• **Tyndall effect** — scattering of light by colloidal particles; visible cone of light.',
        hindi: '• **टाइन्डल प्रभाव** - कोलाइडल कणों द्वारा प्रकाश का प्रकीर्णन; प्रकाश का दृश्यमान शंकु.',
            },
            {
              english: '• **Brownian motion** — zig-zag motion due to molecular collisions; helps colloids stay suspended.',
        hindi: '• **ब्राउनियन गति** - आणविक टकराव के कारण ज़िग-ज़ैग गति; कोलाइड्स को निलंबित रहने में मदद करता है।',
            },
            {
              english: '• **Electrophoresis** — movement of colloidal particles under electric field; reveals charge.',
        hindi: '• **वैद्युतकणसंचलन** - विद्युत क्षेत्र के तहत कोलाइडल कणों की गति; आरोप का पता चलता है.',
            },
            {
              english: '• **Coagulation / flocculation** — settling of dispersed phase by adding electrolyte. **Hardy–Schulze rule**: greater the valency of opposite-charge ion, more efficient (Al³⁺ > Mg²⁺ > Na⁺ to coagulate As₂S₃ negative sol).',
        hindi: '• **जमाव/फ्लोक्यूलेशन** - इलेक्ट्रोलाइट जोड़कर बिखरे हुए चरण का निपटान। **हार्डी-शुल्ज़ नियम**: विपरीत-आवेश वाले आयन की संयोजकता जितनी अधिक होगी, उतना अधिक कुशल (Al³⁺ > Mg²⁺ > Na⁺ As₂S₃ ऋणात्मक सोल को जमाने के लिए)।',
            },
            {
              english: '• **Protective colloid**: lyophilic colloid that protects lyophobic from coagulation; quantified by **gold number** (lower gold number ⇒ more protective).',
        hindi: '• **सुरक्षात्मक कोलाइड**: लियोफिलिक कोलाइड जो लियोफोबिक को जमावट से बचाता है; **सोने की संख्या** द्वारा परिमाणित (कम सोने की संख्या ⇒ अधिक सुरक्षात्मक)।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Emulsion & Emulsifier',
        hindi: 'इमल्शन और इमल्सीफायर',
          },
          body: {
            english: 'Liquid–liquid colloid. Two types: **oil-in-water** (O/W; e.g. milk, vanishing cream) and **water-in-oil** (W/O; cold cream, butter). Emulsifier (soap, lecithin) stabilises by reducing interfacial tension. Demulsification by heating, freezing, centrifugation.',
        hindi: 'तरल-तरल कोलाइड। दो प्रकार: **पानी में तेल** (O/W; उदाहरण के लिए दूध, वैनिशिंग क्रीम) और **तेल में पानी** (W/O; कोल्ड क्रीम, मक्खन)। इमल्सीफायर (साबुन, लेसिथिन) इंटरफेशियल तनाव को कम करके स्थिर करता है। हीटिंग, फ्रीजिंग, सेंट्रीफ्यूजेशन द्वारा विमुद्रीकरण।',
          },
        },
      ],
    },
    {
      id: 'applications',
      title: {
        english: 'Applications',
        hindi: 'अनुप्रयोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Cleansing action of soaps — micelle formation (CMC ≈ 10⁻³ M), tail dissolves grease, head outside in water.',
        hindi: 'साबुन की सफाई क्रिया - मिसेल गठन (सीएमसी ≈ 10⁻³ एम), पूंछ ग्रीस को घोलती है, सिर बाहर पानी में।',
            },
            {
              english: 'Sewage disposal — coagulation by adding alum.',
        hindi: 'सीवेज निपटान - फिटकरी मिलाकर जमाव।',
            },
            {
              english: 'Cottrell precipitator — removes carbon and dust from chimney smoke (charged plates).',
        hindi: 'कॉटरेल प्रीसिपिटेटर - चिमनी के धुएं (चार्ज्ड प्लेट) से कार्बन और धूल को हटाता है।',
            },
            {
              english: 'Smoke screen — colloidal suspensions in defence.',
        hindi: 'धुआँ स्क्रीन - रक्षा में कोलाइडल निलंबन।',
            },
            {
              english: 'Delta formation — river-borne clay (negatively charged) coagulated by sea-water electrolytes.',
        hindi: 'डेल्टा निर्माण - नदी-जनित मिट्टी (नकारात्मक रूप से चार्ज) समुद्र-जल इलेक्ट्रोलाइट्स द्वारा जमा हुई।',
            },
            {
              english: 'Drug delivery — colloidal medicines (silver sol, milk of magnesia, calamine lotion) act faster.',
        hindi: 'दवा वितरण - कोलाइडल दवाएं (सिल्वर सोल, मिल्क ऑफ मैग्नीशिया, कैलामाइन लोशन) तेजी से काम करती हैं।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\dfrac{x}{m} = k\\,P^{1/n}',
      desc: {
        english: 'Freundlich isotherm',
        hindi: 'फ्रायंडलिच इज़ोटेर्म',
      },
    },
    {
      latex: '\\dfrac{x}{m} = \\dfrac{aP}{1 + bP}',
      desc: {
        english: 'Langmuir isotherm',
        hindi: 'लैंगमुइर इज़ोटेर्म',
      },
    },
    {
      latex: 'Al^{3+} > Mg^{2+} > Na^+',
      desc: {
        english: 'Hardy–Schulze (for −ve sol)',
        hindi: 'हार्डी-शुल्ज़ (−ve sol के लिए)',
      },
    },
    {
      latex: '\\Delta G = \\Delta H - T\\Delta S < 0',
      desc: {
        english: 'Adsorption spontaneity',
        hindi: 'सोखना सहजता',
      },
    },
    {
      latex: '\\text{Colloid size: } 1\\text{–}1000\\ \\text{nm}',
      desc: {
        english: 'Range',
        hindi: 'रेंज',
      },
    },
    {
      latex: '\\text{CMC} \\approx 10^{-3}\\ \\text{M (soap)}',
      desc: {
        english: 'Critical micelle conc.',
        hindi: 'क्रिटिकल मिसेल सांद्रण।',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Hardy-Schulze',
      problem: {
        english: 'Most effective coagulant for an arsenic sulphide sol (negatively charged) is:',
        hindi: 'आर्सेनिक सल्फाइड सोल (नकारात्मक रूप से चार्ज) के लिए सबसे प्रभावी कौयगुलांट है:',
      },
      answer: {
        english: 'AlCl₃',
        hindi: 'AlCl₃',
      },
      solution: {
        english: 'Al³⁺ has highest +ve valency.',
        hindi: 'Al³⁺ की संयोजकता उच्चतम है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Tyndall',
      problem: {
        english: 'Tyndall effect is shown by:',
        hindi: 'टिंडल प्रभाव निम्न द्वारा दिखाया गया है:',
      },
      answer: {
        english: 'Colloidal sols',
        hindi: 'कोलाइडल सॉल',
      },
      solution: {
        english: 'Particles 1–1000 nm scatter visible light.',
        hindi: 'कण 1-1000 एनएम दृश्य प्रकाश बिखेरते हैं।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Catalysis',
      problem: {
        english: 'Catalyst used in Contact process for $H_2SO_4$:',
        hindi: '$H_2SO_4$ के लिए संपर्क प्रक्रिया में प्रयुक्त उत्प्रेरक:',
      },
      answer: {
        english: 'V₂O₅',
        hindi: 'V₂O₅',
      },
      solution: {
        english: 'Modern catalyst replaced Pt.',
        hindi: 'आधुनिक उत्प्रेरक ने पीटी का स्थान ले लिया।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Phys/Chem',
      problem: {
        english: 'High enthalpy of adsorption (~200 kJ/mol) indicates:',
        hindi: 'अधिशोषण की उच्च एन्थैल्पी (~200 kJ/mol) इंगित करती है:',
      },
      answer: {
        english: 'Chemisorption',
        hindi: 'रसायनशोषण',
      },
      solution: {
        english: 'Physisorption $\\Delta H \\sim 20$–40 kJ/mol.',
        hindi: 'फ़िसिसोर्शन $\\Delta H \\sim 20$–40 kJ/mol।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Emulsion',
      problem: {
        english: 'Milk is an example of which type of emulsion?',
        hindi: 'दूध किस प्रकार के इमल्शन का उदाहरण है?',
      },
      answer: {
        english: 'Oil-in-water (O/W)',
        hindi: 'पानी में तेल (O/W)',
      },
      solution: {
        english: 'Liquid fat dispersed in water.',
        hindi: 'तरल वसा पानी में बिखरी हुई। **सिस्टम**: अध्ययनाधीन भाग। **परिवेश**: शेष ब्रह्मांड। सीमा वास्तविक या काल्पनिक हो सकती है।',
      },
    },
  ],
  next: {
    slug: 'metallurgy',
    title: 'General Principles of Metallurgy',
  },
};

export default chapter;
